import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { Search, X, Loader2, MessageSquare } from 'lucide-react'
import { searchInboxMessages } from '@/lib/searchInboxMessages'

/**
 * Inbox message-body search (CP2 — Option B: a separate modal).
 *
 * Mirrors the team-chat SearchMessagesModal. Debounces the input into
 * searchInboxMessages (RLS-relying → workspace-scoped, so a result can never be
 * another workspace's message). Clicking a result opens its conversation via
 * onSelect(conversation_id) — the caller navigates to /app/inbox/:id, which
 * ConversationDetail loads by route param, so it works even for a conversation
 * not in the inbox list. The existing conversation-list client filter is
 * untouched. Consumes searchInboxMessages as-is.
 */

const DEBOUNCE_MS = 250
const TYPE_LABEL: Record<string, string> = { inbound: 'Inbound', outbound: 'Reply', note: 'Note', system: 'System' }

export default function SearchInboxModal({
  onClose, onSelect,
}: {
  onClose: () => void
  onSelect: (conversationId: string) => void
}) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  const hasQuery = debounced.length > 0
  const { data: results = [], isLoading, isError } = useQuery({
    queryKey: ['inbox-message-search', debounced],
    enabled: hasQuery,
    queryFn: () => searchInboxMessages(debounced),
  })

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 100, padding: 24, paddingTop: '10vh' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Search input */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Search style={{ width: 18, height: 18, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') onClose() }}
            placeholder="Search message content…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 15, color: 'var(--ink-primary)' }}
          />
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', flexShrink: 0 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Results / states */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {!hasQuery && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>
              Search across all message content in your inbox.
            </div>
          )}

          {hasQuery && isLoading && (
            <div style={{ padding: '40px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-tertiary)' }}>
              <Loader2 style={{ width: 16, height: 16, marginRight: 8 }} className="animate-spin" />
              <span style={{ fontSize: 13 }}>Searching…</span>
            </div>
          )}

          {hasQuery && !isLoading && isError && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-secondary)', fontSize: 13 }}>
              Something went wrong searching messages. Please try again.
            </div>
          )}

          {hasQuery && !isLoading && !isError && results.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>
              No messages found for “{debounced}”.
            </div>
          )}

          {hasQuery && !isLoading && !isError && results.length > 0 && (
            <div style={{ padding: '8px 0' }}>
              {results.map(r => (
                <button
                  key={r.id}
                  onClick={() => onSelect(r.conversation_id)}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '10px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-tertiary)', minWidth: 0 }}>
                    <span style={{ fontWeight: 700, color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.sender_name || 'Unknown'}</span>
                    {r.type && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}><MessageSquare style={{ width: 11, height: 11 }} />{TYPE_LABEL[r.type] ?? r.type}</span>}
                    {r.created_at && <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.content}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}
